import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Rating from '@mui/material/Rating';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import dayjs from 'dayjs';

import {
  BreadcrumbHeader,
  DataTable,
  EmptyState,
  SupplierQuotesModal,
  ActionMenu,
  StatCard,
  FilterTabs } from
'../../components/shared';

import { partOrders, supplierQuotes, quoteHistory, parts, suppliers, supplierRatings } from '../../services/dataClient';
import '../../styles/design-tokens.css';

// ─── Style constants ──────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const thSx = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--grey-600)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  backgroundColor: 'var(--secondary-light)',
  borderBottom: '1px solid var(--grey-200)',
  py: '10px',
  whiteSpace: 'nowrap'
};

const subThSx = {
  fontSize: '0.625rem',
  fontWeight: 600,
  color: 'var(--grey-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid var(--grey-100)',
  py: '6px'
};

// ─── Quotes tab: response status helpers ─────────────────────────────────────
function getResponseStatus(quotes) {
  if (quotes.some((q) => q.status === 'Accepted')) return { label: 'Accepted', bg: '#E8F5E9', color: '#2E7D32' };
  const total = quotes.length;
  const received = quotes.filter((q) => q.status !== 'Pending').length;
  if (received === 0) return { label: 'Pending', bg: '#ECEFF1', color: '#546E7A' };
  if (received < total) return { label: 'Partial', bg: '#FFF8E1', color: '#F57F17' };
  return { label: 'All Responded', bg: '#E0F2F1', color: '#00695C' };
}

function ResponseStatusBadge({ quotes }) {
  const { label, bg, color } = getResponseStatus(quotes);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '3px', borderRadius: '12px', backgroundColor: bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>);

}

// ─── Pricing tab: module-level data ──────────────────────────────────────────
const allRawRecords = [
...quoteHistory.map((h) => ({ ...h, isActiveRFQ: false })),
...supplierQuotes
  .filter((q) => q.unitCost !== null && q.status !== 'Pending')
  .map((q) => ({
  id: q.id,
  partNumber: q.partNumber,
  ticketNumber: q.ticketNumber,
  supplierName: q.supplierName,
  unitCost: q.unitCost,
  leadTimeDays: q.leadTimeDays,
  responseDate: q.responseDate,
  status: q.status,
  isActiveRFQ: true,
  partOrderId: q.partOrderId
}))];

const partDescMap = {};
const partIdMap = {};
parts.forEach((p) => {
  partDescMap[p.partNumber] = p.description;
  partIdMap[p.partNumber] = p.id;
});

const activeRFQPartSet = new Set(supplierQuotes.map((q) => q.partNumber));

function buildByPart() {
  const map = {};
  allRawRecords.forEach((r) => {
    if (!map[r.partNumber]) map[r.partNumber] = [];
    map[r.partNumber].push(r);
  });

  return Object.entries(map).map(([partNumber, records]) => {
    const accepted = records.filter((r) => r.status === 'Accepted');
    const bestPrice = accepted.length > 0 ? Math.min(...accepted.map((r) => r.unitCost)) : null;
    const bestRecord = bestPrice !== null ? accepted.find((r) => r.unitCost === bestPrice) : null;
    const avgPrice = accepted.length > 0 ?
    accepted.reduce((s, r) => s + r.unitCost, 0) / accepted.length :
    null;
    const lastQuoted = records
    .filter((r) => r.responseDate)
    .sort((a, b) => b.responseDate.localeCompare(a.responseDate))[0]?.responseDate ?? null;
    const uniqueSuppliers = [...new Set(records.map((r) => r.supplierName))].sort();
    const hasActiveRFQ = activeRFQPartSet.has(partNumber);

    const sortedAccepted = [...accepted].sort((a, b) => b.responseDate.localeCompare(a.responseDate));
    let priceTrend = null;
    if (sortedAccepted.length >= 2) {
      const diff = sortedAccepted[0].unitCost - sortedAccepted[1].unitCost;
      priceTrend = diff < -0.01 ? 'down' : diff > 0.01 ? 'up' : 'flat';
    }

    const winRateMap = {};
    uniqueSuppliers.forEach((sup) => {
      const total = records.filter((r) => r.supplierName === sup).length;
      const wins = records.filter((r) => r.supplierName === sup && r.status === 'Accepted').length;
      winRateMap[sup] = { total, wins };
    });

    return {
      partNumber,
      description: partDescMap[partNumber] ?? '—',
      records: [...records].sort((a, b) => (b.responseDate ?? '').localeCompare(a.responseDate ?? '')),
      uniqueSuppliers,
      winRateMap,
      quoteCount: records.length,
      bestPrice,
      bestRecord,
      avgPrice,
      lastQuoted,
      hasActiveRFQ,
      priceTrend,
      acceptedCount: accepted.length
    };
  });
}

const BY_PART_DATA = buildByPart();

// ─── Ratings tab: module-level data ──────────────────────────────────────────
function buildBySupplier() {
  return suppliers.map((sup) => {
    const historyRecords = quoteHistory
    .filter((h) => h.supplierName === sup.name)
    .map((h) => ({ ...h }));

    const activeRecords = supplierQuotes
    .filter((q) => q.supplierName === sup.name && q.unitCost !== null && q.status !== 'Pending')
    .map((q) => ({
      id: q.id,
      partNumber: q.partNumber,
      ticketNumber: q.ticketNumber,
      supplierName: q.supplierName,
      unitCost: q.unitCost,
      leadTimeDays: q.leadTimeDays,
      responseDate: q.responseDate,
      status: q.status
    }));

    const allQuotes = [...historyRecords, ...activeRecords].sort(
      (a, b) => (b.responseDate ?? '').localeCompare(a.responseDate ?? '')
    );

    const totalQuotes = allQuotes.length;
    const wonQuotes = allQuotes.filter((q) => q.status === 'Accepted').length;
    const winRate = totalQuotes > 0 ? (wonQuotes / totalQuotes * 100).toFixed(1) : null;

    const timedQuotes = allQuotes.filter((q) => q.leadTimeDays != null);
    const avgLeadTime = timedQuotes.length > 0 ?
    Math.round(timedQuotes.reduce((s, q) => s + q.leadTimeDays, 0) / timedQuotes.length * 10) / 10 :
    null;

    return { ...sup, totalQuotes, wonQuotes, winRate, avgLeadTime, allQuotes };
  });
}

const BY_SUPPLIER_DATA = buildBySupplier();

// ─── Pricing tab filter options ───────────────────────────────────────────────
const STATUS_TABS = [
{ value: 'all', label: 'All Parts' },
{ value: 'accepted', label: 'Has Accepted' },
{ value: 'declined', label: 'No Winner Yet' },
{ value: 'active', label: 'Has Active RFQ' }];

// ─── Helper components ────────────────────────────────────────────────────────
function PriceTrendIcon({ trend }) {
  if (trend === 'down') return <TrendingDownIcon sx={{ fontSize: 14, color: '#2E7D32', ml: '4px', verticalAlign: 'middle' }} />;
  if (trend === 'up') return <TrendingUpIcon sx={{ fontSize: 14, color: '#C62828', ml: '4px', verticalAlign: 'middle' }} />;
  if (trend === 'flat') return <TrendingFlatIcon sx={{ fontSize: 14, color: 'var(--grey-400)', ml: '4px', verticalAlign: 'middle' }} />;
  return null;
}

function CompactRating({ ratings }) {
  if (!ratings.length) return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>;
  const avg = ratings.reduce((s, r) => s + r.rating, 0) / ratings.length;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Rating
        value={avg}
        readOnly
        precision={0.5}
        size="small"
        sx={{ color: 'var(--warning-dark)', '& .MuiRating-iconEmpty': { color: 'var(--grey-300)' } }} />
      
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
        ({ratings.length})
      </Typography>
    </Box>);

}

// Break at the last space within `max` chars, or hard-cut if no space
function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SupplierQuotesPage() {
  const navigate = useNavigate();

  // ── Shared ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('quotes');

  // ── Quotes tab ────────────────────────────────────────────────────────────
  const [modalOrder, setModalOrder] = useState(null);

  const quotesByOrder = useMemo(() => {
    const map = {};
    supplierQuotes.forEach((q) => {
      if (!map[q.partOrderId]) map[q.partOrderId] = [];
      map[q.partOrderId].push(q);
    });
    return map;
  }, []);

  const rows = useMemo(() => {
    const orderIds = new Set([
    ...Object.keys(quotesByOrder),
    ...partOrders.filter((o) => o.status === 'Awaiting Quote').map((o) => o.id)]
    );
    return Array.from(orderIds)
    .map((orderId) => {
      const order = partOrders.find((o) => o.id === orderId);
      if (!order) return null;
      const quotes = quotesByOrder[orderId] ?? [];
      const received = quotes.filter((q) => q.status !== 'Pending').length;
      const sentDate = quotes[0]?.sentDate ?? order.createdDate;
      return { order, quotes, received, sentDate };
    })
    .filter(Boolean)
    .sort((a, b) => a.sentDate < b.sentDate ? 1 : -1);
  }, [quotesByOrder]);

  const quotesStats = useMemo(() => {
    const activeRFQs = rows.filter((r) => r.quotes.some((q) => q.status === 'Pending')).length;
    const awaitingResponse = supplierQuotes.filter((q) => q.status === 'Pending').length;
    const responsesReceived = supplierQuotes.filter((q) => q.status === 'Received').length;
    const thisMonth = dayjs().format('YYYY-MM');
    const acceptedThisMonth = supplierQuotes.filter(
      (q) => q.status === 'Accepted' && (q.responseDate ?? '').startsWith(thisMonth)
    ).length;
    return { activeRFQs, awaitingResponse, responsesReceived, acceptedThisMonth };
  }, [rows]);

  const quotesColumns = [
  {
    field: 'ticketNumber',
    headerName: 'Ticket #',
    sortable: false,
    renderCell: (row) =>
    <Typography
      onClick={() => setModalOrder(row.order)}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
      
          {row.order.ticketNumber}
        </Typography>

  },
  {
    field: 'partNumber',
    headerName: 'Part #',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
          {row.order.partNumber}
        </Typography>

  },
  {
    field: 'resellerName',
    headerName: 'Reseller',
    sortable: false,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.order.resellerName);
      return (
        <Tooltip title={truncated ? row.order.resellerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: row.order.resellerName ? 'var(--grey-800)' : 'var(--grey-400)' }}>
              {row.order.resellerName ? short : '—'}
            </Typography>
          </Tooltip>);

    }
  },
  {
    field: 'customerName',
    headerName: 'End-Customer',
    sortable: false,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.order.customerName);
      return (
        <Tooltip title={truncated ? row.order.customerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
              {short}
            </Typography>
          </Tooltip>);

    }
  },
  {
    field: 'sentDate',
    headerName: 'Sent',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>
          {row.sentDate ?? '—'}
        </Typography>

  },
  {
    field: 'suppliers',
    headerName: 'Suppliers',
    sortable: false,
    renderCell: (row) => {
      if (row.quotes.length === 0) {
        return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>;
      }
      const accepted = row.quotes.find((q) => q.status === 'Accepted');
      const primaryName = accepted ? accepted.supplierName : row.quotes[0].supplierName;
      const othersCount = row.quotes.length - 1;
      const { short, truncated } = truncate(primaryName);
      return (
        <Tooltip title={truncated ? primaryName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography sx={{ fontSize: '0.875rem',
              fontWeight: accepted ? 600 : 400,
              color: accepted ? '#1B5E20' : 'var(--grey-700)'
            }}>
                {short}
              </Typography>
              {othersCount > 0 &&
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', flexShrink: 0 }}>
                  +{othersCount} other{othersCount !== 1 ? 's' : ''}
                </Typography>
            }
            </Box>
          </Tooltip>);

    }
  },
  {
    field: 'responses',
    headerName: 'Responses',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-800)' }}>
          {row.quotes.length > 0 ? `${row.received} / ${row.quotes.length}` : '—'}
        </Typography>

  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    renderCell: (row) =>
    row.quotes.length > 0 ?
    <ResponseStatusBadge quotes={row.quotes} /> :

    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>

  },
  {
    field: '_action',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <ActionMenu
      actions={[
      { label: 'View Quotes', onClick: () => setModalOrder(row.order) },
      { label: 'View Order', onClick: () => navigate(`/orders/parts/${row.order.id}`) }]
      } />

  }];

  // ── Pricing tab ───────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPart, setExpandedPart] = useState(null);

  const allSupplierNames = useMemo(
    () => [...new Set(allRawRecords.map((r) => r.supplierName))].sort(),
    []
  );

  const pricingStats = useMemo(() => {
    const partsTracked = BY_PART_DATA.length;
    const totalRecords = allRawRecords.length;
    const supplierCounts = {};
    allRawRecords.forEach((r) => {
      supplierCounts[r.supplierName] = (supplierCounts[r.supplierName] ?? 0) + 1;
    });
    const mostActive = Object.entries(supplierCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
    const savingsPcts = BY_PART_DATA
    .filter((p) => p.bestPrice !== null && p.records.some((r) => r.unitCost > p.bestPrice))
    .map((p) => {
      const highest = Math.max(...p.records.map((r) => r.unitCost));
      return (highest - p.bestPrice) / highest * 100;
    });
    const avgSavings = savingsPcts.length > 0 ?
    (savingsPcts.reduce((s, v) => s + v, 0) / savingsPcts.length).toFixed(1) :
    '0.0';
    return { partsTracked, totalRecords, mostActive, avgSavings };
  }, []);

  const filteredParts = useMemo(() => {
    return BY_PART_DATA.filter((p) => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!p.partNumber.toLowerCase().includes(q) && !p.description.toLowerCase().includes(q)) return false;
      }
      if (supplierFilter !== 'all' && !p.uniqueSuppliers.includes(supplierFilter)) return false;
      if (statusFilter === 'accepted' && p.acceptedCount === 0) return false;
      if (statusFilter === 'declined' && p.acceptedCount > 0) return false;
      if (statusFilter === 'active' && !p.hasActiveRFQ) return false;
      return true;
    }).sort((a, b) => (b.lastQuoted ?? '').localeCompare(a.lastQuoted ?? ''));
  }, [searchText, supplierFilter, statusFilter]);

  // ── Ratings tab ───────────────────────────────────────────────────────────
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const localRatings = supplierRatings;

  const ratingsStats = useMemo(() => {
    const activeCount = suppliers.filter((s) => s.active).length;
    const ratedSuppliers = BY_SUPPLIER_DATA.filter((s) =>
    localRatings.some((r) => r.supplierName === s.name)
    );
    const avgRating = ratedSuppliers.length > 0 ?
    (ratedSuppliers.reduce((sum, s) => {
      const rs = localRatings.filter((r) => r.supplierName === s.name);
      return sum + rs.reduce((a, r) => a + r.rating, 0) / rs.length;
    }, 0) / ratedSuppliers.length).toFixed(1) :
    '—';
    const bestWinRate = BY_SUPPLIER_DATA
    .filter((s) => s.winRate !== null)
    .sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate))[0] ?? null;
    const timedSuppliers = BY_SUPPLIER_DATA.filter((s) => s.avgLeadTime !== null);
    const avgLeadTimeOverall = timedSuppliers.length > 0 ?
    (timedSuppliers.reduce((s, x) => s + x.avgLeadTime, 0) / timedSuppliers.length).toFixed(1) :
    '—';
    return { activeCount, avgRating, bestWinRate, avgLeadTimeOverall };
  }, [localRatings]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <BreadcrumbHeader
        title="Supplier Quotes"
        subtitle="Active RFQs, pricing history, and supplier performance"
        crumbs={[{ label: 'Orders' }, { label: 'Supplier Quotes' }]} />

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <ContentCard sx={{ px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ minHeight: 44,
            '& .MuiTab-root': {
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: 'var(--grey-600)',
              minHeight: 44,
              py: '10px'
            },
            '& .Mui-selected': { color: 'var(--secondary-main)', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--secondary-main)' }
          }}>
          
          <Tab value="quotes" label="Quotes" disableRipple />
          <Tab value="pricing" label="Pricing" disableRipple />
          <Tab value="ratings" label="Ratings" disableRipple />
        </Tabs>
      </ContentCard>

      {/* ── KPI stat cards (swap per tab) ─────────────────────────────────── */}
      {activeTab === 'quotes' &&
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <StatCard label="Active RFQs" value={quotesStats.activeRFQs} />
          <StatCard label="Awaiting Response" value={quotesStats.awaitingResponse} />
          <StatCard label="Responses Received" value={quotesStats.responsesReceived} />
          <StatCard label="Accepted This Month" value={quotesStats.acceptedThisMonth} />
        </Box>
      }
      {activeTab === 'pricing' &&
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <StatCard label="Parts Tracked" value={pricingStats.partsTracked} />
          <StatCard label="Total Quote Records" value={pricingStats.totalRecords} />
          <StatCard label="Most Active Supplier" value={pricingStats.mostActive} />
          <StatCard
          label="Avg Savings vs Highest Quote"
          value={`${pricingStats.avgSavings}%`}
          trend="down"
          trendLabel="by choosing lowest accepted" />
        
        </Box>
      }
      {activeTab === 'ratings' &&
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <StatCard label="Active Suppliers" value={ratingsStats.activeCount} />
          <StatCard label="Avg Supplier Rating" value={ratingsStats.avgRating} />
          <StatCard
          label="Best Win Rate"
          value={ratingsStats.bestWinRate ? `${ratingsStats.bestWinRate.winRate}%` : '—'}
          trendLabel={ratingsStats.bestWinRate?.name ?? ''} />
        
          <StatCard
          label="Avg Quote Lead Time"
          value={ratingsStats.avgLeadTimeOverall !== '—' ? `${ratingsStats.avgLeadTimeOverall}d` : '—'} />
        
        </Box>
      }

      {/* ── Main content card ─────────────────────────────────────────────── */}
      <ContentCard
        sx={{ p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>

        {/* ══ QUOTES TAB ═════════════════════════════════════════════════════ */}
        {activeTab === 'quotes' &&
        <>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
              All orders where supplier quotes have been requested. Click any row to review responses and accept a quote.
            </Typography>
            <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
              {rows.length === 0 ?
            <EmptyState
              title="No quotes yet"
              description="When you request supplier quotes from a part order, they'll appear here." /> :

            <DataTable
              columns={quotesColumns}
              data={rows}
              onRowClick={(row) => setModalOrder(row.order)} />

            }
            </Box>
          </>
        }

        {/* ══ PRICING TAB ════════════════════════════════════════════════════ */}
        {activeTab === 'pricing' &&
        <>
            {/* Filter controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
              size="small"
              placeholder="Search part # or description…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
              InputProps={{
                startAdornment:
                <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                    </InputAdornment>

              }} />
            
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Supplier</InputLabel>
                <Select
                value={supplierFilter}
                label="Supplier"
                onChange={(e) => setSupplierFilter(e.target.value)}>
                
                  <MenuItem value="all">All Suppliers</MenuItem>
                  {allSupplierNames.map((s) =>
                <MenuItem key={s} value={s}>{s}</MenuItem>
                )}
                </Select>
              </FormControl>
            </Box>

            <FilterTabs tabs={STATUS_TABS} value={statusFilter} onChange={(v) => setStatusFilter(v)} />

            <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 28, pr: 0 }} />
                    <TableCell sx={{ ...thSx, pl: 2 }}>Part #</TableCell>
                    <TableCell sx={thSx}>Description</TableCell>
                    <TableCell sx={thSx}>Suppliers</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: 'center' }}>Quotes</TableCell>
                    <TableCell sx={thSx}>Best Accepted Price</TableCell>
                    <TableCell sx={thSx}>Avg Accepted</TableCell>
                    <TableCell sx={thSx}>Last Quoted</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: 'center' }}>Active RFQ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredParts.length === 0 &&
                <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 5, color: 'var(--grey-400)', fontSize: '0.875rem' }}>
                        No parts match the current filters.
                      </TableCell>
                    </TableRow>
                }
                  {filteredParts.map((p) => {
                  const isExpanded = expandedPart === p.partNumber;
                  const partId = partIdMap[p.partNumber];
                  const winRateTip = p.uniqueSuppliers
                  .map((s) => {
                    const { wins, total } = p.winRateMap[s];
                    return `${s}: ${wins} win${wins !== 1 ? 's' : ''} / ${total} quote${total !== 1 ? 's' : ''}`;
                  })
                  .join('\n');

                  return (
                    <>
                        <TableRow
                        key={p.partNumber}
                        onClick={() => setExpandedPart((prev) => prev === p.partNumber ? null : p.partNumber)}
                        sx={{ cursor: 'pointer',
                          borderLeft: p.hasActiveRFQ ? '3px solid #FB8C00' : '3px solid transparent',
                          '&:hover': { backgroundColor: '#f8fafc' },
                          '& td': {
                            borderBottom: isExpanded ? 'none' : '1px solid var(--grey-200)',
                            py: '12px',
                            fontSize: '0.875rem'
                          }
                        }}>
                        
                          <TableCell sx={{ pr: 0, pl: '6px', color: 'var(--grey-400)', width: 28 }}>
                            {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          </TableCell>
                          <TableCell sx={{ pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Typography
                              onClick={(e) => {e.stopPropagation();if (partId) navigate(`/inventory/search/${partId}`);}}
                              sx={{ fontSize: '0.875rem',
                                fontFamily: 'monospace',
                                color: partId ? '#009688' : 'var(--grey-700)',
                                fontWeight: 500,
                                cursor: partId ? 'pointer' : 'default',
                                '&:hover': partId ? { textDecoration: 'underline' } : {}
                              }}>
                              
                                {p.partNumber}
                              </Typography>
                              {partId && <ArrowOutwardIcon sx={{ fontSize: 12, color: 'var(--grey-400)' }} />}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: 'var(--grey-700)' }}>{p.description}</TableCell>
                          <TableCell>
                            <Tooltip
                            title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>{winRateTip}</Box>}
                            placement="top" arrow>
                            
                              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', cursor: 'default' }}>
                                {p.uniqueSuppliers.slice(0, 2).join(', ')}
                                {p.uniqueSuppliers.length > 2 &&
                              <Typography component="span" sx={{ color: 'var(--grey-400)', fontSize: '0.8125rem' }}>
                                    {' '}+{p.uniqueSuppliers.length - 2} more
                                  </Typography>
                              }
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--grey-100)', borderRadius: '10px', px: '10px', py: '2px', minWidth: 28 }}>
                              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-600)' }}>{p.quoteCount}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {p.bestPrice !== null ?
                          <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#009688' }}>${p.bestPrice.toFixed(2)}</Typography>
                                  <PriceTrendIcon trend={p.priceTrend} />
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', mt: '1px' }}>{p.bestRecord?.supplierName}</Typography>
                              </Box> :

                          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                          }
                          </TableCell>
                          <TableCell>
                            {p.avgPrice !== null ?
                          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', fontWeight: 500 }}>${p.avgPrice.toFixed(2)}</Typography> :

                          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                          }
                          </TableCell>
                          <TableCell sx={{ color: 'var(--grey-600)' }}>
                            {p.lastQuoted ? dayjs(p.lastQuoted).format('MMM D, YYYY') : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {p.hasActiveRFQ ?
                          <Box
                            onClick={(e) => {e.stopPropagation();navigate('/orders/quotes');}}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#FFF3E0', cursor: 'pointer', '&:hover': { backgroundColor: '#FFE0B2' } }}>
                            
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#FB8C00', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#E65100' }}>Active</Typography>
                              </Box> :

                          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                          }
                          </TableCell>
                        </TableRow>

                        {/* Expanded sub-table */}
                        <TableRow key={`${p.partNumber}-expand`} sx={{ height: 0 }}>
                          <TableCell colSpan={9} sx={{ p: 0, pb: 0, lineHeight: 0, borderBottom: isExpanded ? '2px solid var(--grey-200)' : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ backgroundColor: '#fafcff', borderTop: '1px solid var(--grey-100)' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ ...subThSx, pl: '56px', width: 150 }}>Date</TableCell>
                                      <TableCell sx={{ ...subThSx, width: 100 }}>Ticket #</TableCell>
                                      <TableCell sx={{ ...subThSx, width: 180 }}>Supplier</TableCell>
                                      <TableCell sx={{ ...subThSx, width: 100 }}>Unit Cost</TableCell>
                                      <TableCell sx={{ ...subThSx, width: 110 }}>Status</TableCell>
                                      <TableCell sx={{ ...subThSx, width: 110 }}>Source</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {p.records.map((r, i) => {
                                    const isBest = r.status === 'Accepted' && r.unitCost === p.bestPrice;
                                    return (
                                      <TableRow
                                        key={r.id}
                                        sx={{ borderLeft: isBest ? '3px solid #43A047' : '3px solid transparent',
                                          '& td': {
                                            borderBottom: i < p.records.length - 1 ? '1px solid var(--grey-100)' : 'none',
                                            py: '8px',
                                            fontSize: '0.8125rem'
                                          }
                                        }}>
                                        
                                          <TableCell sx={{ pl: '56px', width: 150, color: 'var(--grey-600)', whiteSpace: 'nowrap' }}>
                                            {r.responseDate ? dayjs(r.responseDate).format('MMM D, YYYY') : '—'}
                                          </TableCell>
                                          <TableCell
                                          onClick={(e) => {e.stopPropagation();if (r.partOrderId) navigate(`/orders/parts/${r.partOrderId}`);}}
                                          sx={{ fontFamily: 'monospace', color: r.partOrderId ? '#009688' : 'var(--grey-500)', cursor: r.partOrderId ? 'pointer' : 'default', '&:hover': r.partOrderId ? { textDecoration: 'underline' } : {} }}>
                                          
                                            {r.ticketNumber}
                                          </TableCell>
                                          <TableCell sx={{ color: 'var(--grey-700)' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                              {r.supplierName}
                                              {isBest &&
                                            <Box sx={{ px: '6px', py: '1px', borderRadius: '4px', backgroundColor: '#E8F5E9', display: 'inline-flex', alignItems: 'center' }}>
                                                  <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '0.03em' }}>✓ Best</Typography>
                                                </Box>
                                            }
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#009688' }}>${r.unitCost.toFixed(2)}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            {r.status === 'Accepted' &&
                                          <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: '#E8F5E9' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2E7D32' }}>Accepted</Typography>
                                              </Box>
                                          }
                                            {r.status === 'Declined' &&
                                          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>Declined</Typography>
                                          }
                                            {r.status === 'Received' &&
                                          <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: '#E3F2FD' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1565C0' }}>Received</Typography>
                                              </Box>
                                          }
                                          </TableCell>
                                          <TableCell>
                                            {r.isActiveRFQ ?
                                          <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: '#FFF3E0' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#E65100' }}>Active RFQ</Typography>
                                              </Box> :

                                          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>Historical</Typography>
                                          }
                                          </TableCell>
                                        </TableRow>);

                                  })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </>);

                })}
                </TableBody>
              </Table>
            </Box>
          </>
        }

        {/* ══ RATINGS TAB ════════════════════════════════════════════════════ */}
        {activeTab === 'ratings' &&
        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 28, pr: 0 }} />
                  <TableCell sx={{ ...thSx, pl: 2 }}>Supplier</TableCell>
                  <TableCell sx={thSx}>Location</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'center' }}>Total Quotes</TableCell>
                  <TableCell sx={thSx}>Won / Win Rate</TableCell>
                  <TableCell sx={thSx}>Avg Lead Time</TableCell>
                  <TableCell sx={thSx}>Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {BY_SUPPLIER_DATA.map((sup) => {
                const isExpanded = expandedSupplier === sup.id;
                const supRatings = localRatings.filter((r) => r.supplierName === sup.name);

                return (
                  <>
                      <TableRow
                      key={sup.id}
                      onClick={() => setExpandedSupplier((prev) => prev === sup.id ? null : sup.id)}
                      sx={{ cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f8fafc' },
                        '& td': {
                          borderBottom: isExpanded ? 'none' : '1px solid var(--grey-200)',
                          py: '12px',
                          fontSize: '0.875rem'
                        }
                      }}>
                      
                        <TableCell sx={{ pr: 0, pl: '6px', color: 'var(--grey-400)', width: 28 }}>
                          {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                        </TableCell>
                        <TableCell sx={{ pl: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>
                                {sup.name}
                              </Typography>
                              <Chip
                              label={sup.active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{ height: 18,
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                backgroundColor: sup.active ? '#E8F5E9' : 'var(--grey-100)',
                                color: sup.active ? '#2E7D32' : 'var(--grey-500)',
                                '& .MuiChip-label': { px: '6px' }
                              }} />
                            
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', fontFamily: 'monospace' }}>
                              {sup.code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'var(--grey-600)' }}>{sup.city}, {sup.state}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--grey-100)', borderRadius: '10px', px: '10px', py: '2px', minWidth: 28 }}>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-600)' }}>{sup.totalQuotes}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {sup.winRate !== null ?
                        <Box>
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-800)' }}>{sup.winRate}%</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', mt: '1px' }}>{sup.wonQuotes} of {sup.totalQuotes} won</Typography>
                            </Box> :

                        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                        }
                        </TableCell>
                        <TableCell>
                          {sup.avgLeadTime !== null ?
                        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>{sup.avgLeadTime}d</Typography> :

                        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-300)' }}>—</Typography>
                        }
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <CompactRating ratings={supRatings} />
                        </TableCell>
                      </TableRow>

                      {/* Expanded reviews */}
                      <TableRow key={`${sup.id}-expand`} sx={{ height: 0 }}>
                        <TableCell colSpan={7} sx={{ p: 0, pb: 0, lineHeight: 0, borderBottom: isExpanded ? '2px solid var(--grey-200)' : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ backgroundColor: 'var(--grey-50)', borderTop: '1px solid var(--grey-100)', px: 3, py: 2 }}>
                              {supRatings.length === 0 ?
                            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                  No ratings yet.
                                </Typography> :

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  {supRatings.map((r) =>
                              <Box
                                key={r.id}
                                sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', backgroundColor: 'var(--paper)', border: '1px solid var(--grey-200)', borderRadius: '6px', px: 2, py: 1.5 }}>
                                
                                      <Box sx={{ minWidth: 110, flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                                          {dayjs(r.ratedAt).format('MMM D, YYYY')}
                                        </Typography>
                                        {r.ticketNumber &&
                                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', fontFamily: 'monospace', mt: '2px' }}>
                                            {r.ticketNumber}
                                          </Typography>
                                  }
                                      </Box>
                                      <Box sx={{ flexShrink: 0 }}>
                                        <Rating
                                    value={r.rating}
                                    readOnly
                                    size="small"
                                    sx={{ color: 'var(--warning-dark)', '& .MuiRating-iconEmpty': { color: 'var(--grey-300)' } }} />
                                  
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)', lineHeight: 1.5 }}>
                                          {r.review}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', mt: '4px' }}>
                                          — {r.ratedBy}
                                        </Typography>
                                      </Box>
                                    </Box>
                              )}
                                </Box>
                            }
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>);

              })}
              </TableBody>
            </Table>
          </Box>
        }
      </ContentCard>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <SupplierQuotesModal
        open={Boolean(modalOrder)}
        order={modalOrder}
        onClose={() => setModalOrder(null)}
        onStatusChange={() => setModalOrder(null)} />
      
    </Box>);

}